import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Eye, EyeOff, Loader2, Users, Clock, Shield } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_eeeed7c0-b14c-41e9-a1b8-b122278f0c69/artifacts/auk1nki7_LOGO-EECASA.png";
const HERO_IMAGE = "https://images.pexels.com/photos/7551617/pexels-photo-7551617.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2";

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  
  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password, rememberMe);
    
    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'Inter Tight', system-ui, sans-serif" }}>
      {/* Left Side - Hero Image & Branding */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0" style={{ 
          background: 'linear-gradient(135deg, rgba(232,1,20,0.85) 0%, rgba(192,0,16,0.75) 50%, rgba(26,20,20,0.9) 100%)' 
        }} />
        
        {/* Mesh Pattern Overlay */}
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%),
                           radial-gradient(circle at 80% 20%, rgba(255,255,255,0.08) 0%, transparent 40%)`
        }} />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Top - Logo */}
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Especialistas En Casa" className="h-10 object-contain brightness-0 invert" />
          </div>
          
          {/* Center - Main Message */}
          <div className="max-w-lg">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
              <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
              <span className="text-white/90 text-sm font-medium">{t('login.welcomeBack')}</span>
            </div>
            
            <h1 className="text-5xl font-semibold text-white leading-tight mb-6" style={{ letterSpacing: '-0.03em' }}>
              Cuidado médico profesional en la comodidad de tu hogar
            </h1>
            
            <p className="text-white/80 text-lg leading-relaxed">
              Plataforma integral para la gestión de servicios de salud domiciliaria. 
              Conectamos pacientes con especialistas certificados.
            </p>
            
            {/* Stats */}
            <div className="flex gap-8 mt-10 pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.15)' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.1)' }}>
                  <Clock className="w-5 h-5 text-white/90" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">24/7</p>
                  <p className="text-sm text-white/60">Atención Continua</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.1)' }}>
                  <Users className="w-5 h-5 text-white/90" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">100+</p>
                  <p className="text-sm text-white/60">Especialistas</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.1)' }}>
                  <Shield className="w-5 h-5 text-white/90" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">5000+</p>
                  <p className="text-sm text-white/60">Pacientes</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-[45%] flex items-center justify-center p-6 sm:p-8 lg:p-12" style={{ background: 'var(--bg, #FAF8F6)' }}>
        <div className="w-full max-w-md">
          {/* Logo for mobile */}
          <div className="flex justify-center mb-8 lg:hidden">
            <div className="p-4 rounded-2xl" style={{ 
              background: 'var(--surface, #fff)', 
              boxShadow: 'var(--shadow-md)' 
            }}>
              <img 
                src={LOGO_URL} 
                alt="Especialistas En Casa" 
                className="h-14 object-contain"
              />
            </div>
          </div>
          
          {/* Desktop Logo */}
          <div className="hidden lg:flex justify-center mb-10">
            <img 
              src={LOGO_URL} 
              alt="Especialistas En Casa" 
              className="h-16 object-contain"
            />
          </div>

          {/* Form Card */}
          <div className="rounded-2xl p-8" style={{ 
            background: 'var(--surface, #fff)', 
            boxShadow: '0 4px 24px -4px rgba(74, 32, 32, 0.08), 0 2px 8px -2px rgba(74, 32, 32, 0.04)'
          }}>
            {/* Header */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-semibold mb-2" style={{ color: 'var(--ink, #1A1414)', letterSpacing: '-0.02em' }}>
                {t('login.title')}
              </h2>
              <p style={{ color: 'var(--ink-3, #8B7E7E)', fontSize: '14px' }}>
                {t('login.subtitle')}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <Alert variant="destructive" className="rounded-xl" style={{ 
                  background: 'var(--red-soft, #FBE6E8)', 
                  border: '1px solid rgba(232,1,20,0.2)' 
                }}>
                  <AlertDescription style={{ color: 'var(--red-deep, #C00010)', fontSize: '13px' }}>
                    {error}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" style={{ color: 'var(--ink-2, #4A3F3F)', fontSize: '13px', fontWeight: 500 }}>
                  {t('login.email')}
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('login.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 rounded-xl transition-all duration-200"
                  style={{ 
                    background: 'var(--bg-soft, #F7F4F1)', 
                    border: '1px solid var(--line, #EFE9E5)',
                    fontSize: '14px'
                  }}
                  data-testid="login-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" style={{ color: 'var(--ink-2, #4A3F3F)', fontSize: '13px', fontWeight: 500 }}>
                  {t('login.password')}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('login.passwordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 pr-12 rounded-xl transition-all duration-200"
                    style={{ 
                      background: 'var(--bg-soft, #F7F4F1)', 
                      border: '1px solid var(--line, #EFE9E5)',
                      fontSize: '14px'
                    }}
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors"
                    style={{ color: 'var(--ink-3, #8B7E7E)' }}
                    data-testid="toggle-password"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer" style={{ color: 'var(--ink-2, #4A3F3F)', fontSize: '13px' }}>
                  <input 
                    type="checkbox" 
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 rounded"
                    style={{ accentColor: 'var(--red, #E80114)' }}
                    data-testid="remember-me-checkbox"
                  />
                  {t('login.rememberMe')}
                </label>
                <Link 
                  to="/forgot-password"
                  className="font-medium transition-colors hover:underline"
                  style={{ color: 'var(--red, #E80114)', fontSize: '13px' }}
                  data-testid="forgot-password-link"
                >
                  {t('login.forgotPassword')}
                </Link>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-xl font-semibold text-white transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                style={{ 
                  background: 'linear-gradient(135deg, #E80114 0%, #C00010 100%)',
                  boxShadow: '0 8px 20px -8px rgba(232, 1, 20, 0.5)',
                  fontSize: '14px'
                }}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common:loading', 'Cargando...')}
                  </>
                ) : (
                  t('login.submit')
                )}
              </Button>
            </form>

            {/* Test Credentials */}
            <div className="mt-6 pt-6 text-center" style={{ borderTop: '1px solid var(--line-soft, #F4F0EC)' }}>
              <p style={{ color: 'var(--ink-3, #8B7E7E)', fontSize: '12px' }}>Test credentials:</p>
              <p className="font-mono mt-1" style={{ color: 'var(--ink-2, #4A3F3F)', fontSize: '11px' }}>
                admin@especialistasencasa.com / Admin123!
              </p>
            </div>
          </div>

          {/* Footer */}
          <p className="text-center mt-8" style={{ color: 'var(--ink-3, #8B7E7E)', fontSize: '12px' }}>
            © 2026 Especialistas En Casa. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
